'use client';

import { type CSSProperties, type ReactNode, useState } from 'react';

import Link from 'next/link';

import { PopImage } from '@ps/ui/atoms/pop-image';
import {
  InfiniteAppendList,
  type InfiniteAppendLoadMore,
} from '@ps/ui/molecules/InfiniteAppendList';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { CLIP_CELL_WIDTH } from '../../../series/shared/detail/SeriesClipCells';

/** The rail's scroll container — a horizontal clip rail on the card's dark surface. */
const RAIL_CLASSES = 'no-scrollbar flex gap-2 overflow-x-auto px-4';

/**
 * Course-rail cell width. Resolves to a CSS var the client sets from the MEASURED
 * rail width (see {@link railCellWidthPx}) so a partial cell ALWAYS peeks at any
 * window size; the fallback (SSR / no-JS / pre-measure) is the 3.2-slot peek
 * capped at a fixed max. Passed to `SeriesClipCells` and reused for the trailing
 * load indicator so they align.
 */
export const COURSE_RAIL_CELL_WIDTH = `var(--rail-cell-w, min(${CLIP_CELL_WIDTH}, 160px))`;

/** Dynamic-rail sizing: aim for ~this full-cell width; always leave this fraction peeking. */
const RAIL_TARGET_CELL_PX = 160;
const RAIL_PEEK_FRACTION = 0.25;
const RAIL_GAP_PX = 8;
/** The rail's horizontal `px-4` (both sides) — excluded from the cell-sizing width. */
const RAIL_INLINE_PADDING_PX = 32;

/**
 * Cell width (px) that always leaves a partial cell peeking (the "there's more"
 * cue) while growing the visible count with the rail width: `floor(width/target)`
 * full cells + a fixed peek, capped by the 3.2-slot width so a narrow viewport
 * keeps My Life's feel. Measured because pure CSS can't `floor(width / cell)`
 * (calc can't divide a length by a length).
 */
function railCellWidthPx(availableWidth: number): number {
  const slots = (availableWidth - 3 * RAIL_GAP_PX) / 3.2;
  const fullCols = Math.max(
    1,
    Math.floor(availableWidth / RAIL_TARGET_CELL_PX),
  );
  const withPeek =
    (availableWidth - fullCols * RAIL_GAP_PX) / (fullCols + RAIL_PEEK_FRACTION);
  return Math.min(slots, withPeek);
}

interface Props {
  /** Chapter name. */
  title: string;
  /** Optional chapter banner — fills the header row behind the title. */
  bannerUrl?: string | null;
  /** The chapter detail page — the "View All" target. */
  viewAllHref?: string;
  /** Whether the section opens expanded (the first chapter on page 1). */
  defaultExpanded?: boolean;
  /** Whether clip pages exist beyond the first (drives the rail's trailing spinner). */
  clipsHasMore: boolean;
  /** Bound server action loading the next clip page as an RSC rail batch. */
  loadMoreClips: InfiniteAppendLoadMore;
  /** The first-page rail cells (server-rendered). */
  children: ReactNode;
}

/**
 * One chapter section on the course detail — a collapsible dark card (1:1 with the
 * legacy `ChapterSection` look): the header is the chapter banner (chevron + name +
 * "View All" pill overlaid) when it has one, else a plain dark row; the expanded
 * body is a HORIZONTAL infinite clip rail (server-rendered first page, appended via
 * `loadMoreClips`). "View All" leads to the full paginated chapter page. Collapse
 * is client state; the clip cells + pagination are server-side-first.
 */
export function CourseChapterSection({
  title,
  bannerUrl,
  viewAllHref,
  defaultExpanded = false,
  clipsHasMore,
  loadMoreClips,
  children,
}: Props) {
  const [open, setOpen] = useState(defaultExpanded);
  const [railCellWidth, setRailCellWidth] = useState<string | null>(null);

  // Ref callback (with cleanup) — runs when the rail mounts on expand and
  // disconnects on collapse; the observer also fires on every window resize,
  // keeping a partial cell always peeking as the rail width changes.
  const observeRail = (rail: HTMLDivElement | null) => {
    if (!rail) {
      return;
    }
    const observer = new ResizeObserver(entries => {
      const available =
        (entries[0]?.contentRect.width ?? 0) - RAIL_INLINE_PADDING_PX;
      if (available > 0) {
        setRailCellWidth(`${Math.round(railCellWidthPx(available))}px`);
      }
    });
    observer.observe(rail);
    return () => observer.disconnect();
  };

  return (
    <section className="bg-line overflow-hidden rounded-xl">
      <div className="relative">
        {bannerUrl ? (
          <div className="absolute inset-0 overflow-hidden" aria-hidden>
            <PopImage
              src={bannerUrl}
              alt=""
              fill
              sizes="(min-width: 1024px) 765px, 100vw"
              resizing={{ width: 880, height: 224, fit: 'cover' }}
              resizeMode="cover"
            />
          </div>
        ) : null}
        <div className="relative z-[1] flex h-14 items-center justify-between gap-3 px-4">
          <button
            type="button"
            onClick={() => setOpen(prev => !prev)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {open ? (
              <ChevronUp
                className="text-content-1 h-4 w-4 shrink-0"
                aria-hidden
              />
            ) : (
              <ChevronDown
                className="text-content-1 h-4 w-4 shrink-0"
                aria-hidden
              />
            )}
            <span className="text-content-1 text-sub line-clamp-1 drop-shadow">
              {title}
            </span>
          </button>
          {viewAllHref ? (
            <Link
              href={viewAllHref}
              className="border-brand text-brand-text bg-surface-2 text-label shrink-0 rounded-full border px-4 py-1 backdrop-blur-md"
            >
              View All
            </Link>
          ) : null}
        </div>
      </div>

      {open ? (
        <div
          ref={observeRail}
          className="pb-4"
          style={
            { '--rail-cell-w': railCellWidth ?? undefined } as CSSProperties
          }
        >
          <InfiniteAppendList
            orientation="horizontal"
            className={RAIL_CLASSES}
            initialPage={1}
            initialHasMore={clipsHasMore}
            loadMore={loadMoreClips}
            // Prev/next arrows so fans can scroll a chapter with more clips than
            // fit (PS-842).
            showScrollArrows
            scrollPrevLabel="Previous clips"
            scrollNextLabel="Next clips"
            indicatorClassName="rounded-md bg-raise/5 text-raise/50"
            indicatorStyle={{
              width: COURSE_RAIL_CELL_WIDTH,
              aspectRatio: '2 / 3',
            }}
          >
            {children}
          </InfiniteAppendList>
        </div>
      ) : null}
    </section>
  );
}
